/**
 * Shared onboarding answer vocabulary.
 *
 * ⚠️ WHY THIS LIVES IN THE DESIGN SYSTEM, which is otherwise not where clinical
 * content belongs: every answer persists as a BARE KEY (`"communication"`), so
 * the label beside it in source is the only surviving definition of what a
 * stored answer MEANS. Two copies of that definition can disagree, and when
 * they do, one of them silently rewrites what past clients are recorded as
 * having said (this already happened once to `intimacy` — see
 * `frontend/src/me/labelLock.ts`).
 *
 * `frontend` (which asks the question) and `portal-www` (which shows the
 * therapist the answer) both already depend on `@codel/dova-ds` and on nothing
 * else in common, so this is the only place a SINGLE definition can sit today.
 * `frontend/src/me/onboarding/answerVocabulary.ts` re-exports it, which keeps
 * the label lock and every existing import pointing at this one list.
 *
 * If a package for shared domain vocabulary is ever created, move this there
 * and delete the re-export. Do not copy it.
 */

export const FOCUS_AREA_OPTIONS = [
  { value: "communication", label: "Communicating and handling conflict better" },
  { value: "emotional_closeness", label: "Increasing intimacy and closeness" },
  { value: "appreciated", label: "Feeling appreciated and understood" },
  { value: "rebuild_trust", label: "Rebuilding trust" },
  { value: "teamwork", label: "Working as a team: decisions, stress, money, parenting" },
  { value: "intimacy", label: "Improving sex life and desire" },
  { value: "have_fun", label: "Having more fun together" },
  { value: "something_else", label: "Something else" },
] as const;

export type FocusAreaKey = (typeof FOCUS_AREA_OPTIONS)[number]["value"];

/** Label for a stored key. An UNKNOWN key returns null, never the raw key:
 *  a therapist reading `rebuild_trust` learns nothing, and a key with no
 *  definition on this side is a drift we want visible, not papered over. */
export function focusAreaLabel(key: string): string | null {
  return FOCUS_AREA_OPTIONS.find((o) => o.value === key)?.label ?? null;
}
